<template>
  <div
    ref="el"
    class="app-scroll"
    v-bind="attributes">
    <slot v-bind="{ orientation: store.orientation }" />
  </div>
</template>

<script setup>
  import Lenis from 'lenis'

  const store = useScrollStore()
  const { bus } = useNuxtApp()

  const attributes = ref({
    'data-scroll': '',
    [`data-scroll-${store.orientation}`]: ''
  })

  const onScroll = useThrottleFn(ev => {
    store.setData({
      limit: ev.limit,
      direction: ev.direction,
      percentage: ev.progress,
      x: store.orientation === 'horizontal' ? ev.scroll : null,
      y: store.orientation === 'vertical' ? ev.scroll : null
    })
  }, 100)

  const $el = useTemplateRef('el')

  let _raf = null
  let _lenis = null

  const rafFn = time => {
    _lenis.raf(time)
    _raf = requestAnimationFrame(rafFn)
  }

  const scrollTo = obj => {
    const { target, options = {} } = obj

    if (target == 'top') {
      store.setData({
        direction: null,
        percentage: 0,
        x: store.orientation === 'horizontal' ? 0 : null,
        y: store.orientation === 'vertical' ? 0 : null
      })
    }

    _lenis.scrollTo(target, {
      force: true,
      lock: true,
      ...options
    })
  }

  const stop = () => {
    store.locked = true
    _lenis.stop()
  }

  const start = () => {
    store.locked = false
    _lenis.start()
  }

  const refresh = () => {
    _lenis.resize()

    store.setData({
      limit: _lenis.limit,
      percentage: _lenis.progress,
      x: store.orientation === 'horizontal' ? _lenis.scroll : null,
      y: store.orientation === 'vertical' ? _lenis.scroll : null
    })
  }

  const destroy = () => {
    cancelAnimationFrame(rafFn)
    removeEventListeners()
    _lenis.destroy()
  }

  const reload = () => {
    if (store.locked) {
      start()
    }

    destroy()
    initialize()

    if (store.locked) {
      stop()
    }

    bus.emit('scroll:reinitialized')
  }

  const addScrollListeners = () => {
    _lenis.on('scroll', onScroll)

    if (bus.$ScrollTrigger) {
      _lenis.on('scroll', () => {
        bus.$ScrollTrigger.update()
      })
    }
  }

  const addEventListeners = () => {
    addScrollListeners()
    bus.on('scroll:stop', stop)
    bus.on('scroll:start', start)
    bus.on('scroll:scroll-to', scrollTo)
    bus.on('scroll:refresh', refresh)
    bus.on('scroll:reload', reload)
  }

  const removeEventListeners = () => {
    bus.off('scroll:stop', stop)
    bus.off('scroll:start', start)
    bus.off('scroll:scroll-to', scrollTo)
    bus.off('scroll:refresh', refresh)
    bus.off('scroll:reload', reload)
  }

  const initialize = () => {
    _lenis = new Lenis({
      ...(store.orientation === 'horizontal'
        ? {
          wrapper: $el.value,
          content: $el.value.children[0]
        }
        : {}),
      easing: t => 1 - Math.pow(1 - t, 4),
      duration: 1,
      smoothWheel: true,
      wheelMultiplier: 1.1,
      touchMultiplier: 1.2,
      orientation: store.orientation
    })

    _raf = requestAnimationFrame(rafFn)
    bus.lenis = _lenis
    addEventListeners()
  }
  const route = useRoute()

  onMounted(() => {
    initialize()
    store.initialized = true
    bus.emit('scroll:initialized')
    watch(route, async () => {
      await nextTick()
      refresh()
    })
    watch(() => store.orientation, reload)
    watch(() => store.locked, async nval => {
      if (nval) { stop() }
      if (!nval) { start() }
    })
  })
</script>

<style lang="scss">
  .app-scroll {
    transition: opacity 0.3s;
  }

  // Solo per la modalità orizzontale, dove il wrapper Lenis è .app-scroll.
  // In modalità verticale (window-mode) la classe .lenis finisce su <html>:
  // height: 100% lì bloccherebbe il box di <html> all'altezza del viewport,
  // impedendo al ResizeObserver dell'autoResize di Lenis di rilevare la
  // crescita del contenuto (limite di scroll stantio → footer irraggiungibile).
  .app-scroll.lenis {
    position: relative;
    width: 100%;
    height: 100%;
  }

  html.lenis,
  html.lenis body {
    height: auto;
  }

  [data-scroll-horizontal] {
    @include horizontal-scrollable;
  }

  [data-lenis-prevent] {
    overscroll-behavior: contain;
  }
</style>
